import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

interface LocationData {
  countryCode: string;
  countrySlug: string;
  city: string | null;
  citySlug: string | null;
  latitude: number | null;
  longitude: number | null;
  detectedCitySlug: string | null;
  loading: boolean;
}

const defaultLocation: LocationData = {
  countryCode: "NL",
  countrySlug: "nl",
  city: null,
  citySlug: null,
  latitude: null,
  longitude: null,
  detectedCitySlug: null,
  loading: true,
};

const LocationContext = createContext<LocationData>(defaultLocation);

const COUNTRY_SLUG_MAP: Record<string, string> = {
  NL: "nl",
  BE: "be",
  DE: "de",
  GB: "gb",
  SI: "si",
  AT: "de",
  FR: "fr",
  US: "us",
  AU: "au",
};

export function LocationProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<LocationData>(defaultLocation);

  useEffect(() => {
    let cancelled = false;

    async function detect() {
      try {
        // ── Check localStorage cache first (24h TTL) ─────────
        const CACHE_KEY = "inkv_geo_v2";
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            const age = Date.now() - (parsed._ts || 0);
            if (age < 86_400_000) {
              // 24 uur
              if (!cancelled) setLocation({ ...parsed, loading: false });
              return;
            }
          } catch {}
        }

        // First: try browser geolocation (most accurate)
        const browserGeo = await new Promise<GeolocationCoordinates | null>((resolve) => {
          if (!navigator.geolocation) {
            resolve(null);
            return;
          }
          const timeout = setTimeout(() => resolve(null), 3000);
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              clearTimeout(timeout);
              resolve(pos.coords);
            },
            () => {
              clearTimeout(timeout);
              resolve(null);
            },
            { timeout: 3000, maximumAge: 3600000 },
          );
        });

        // Call geo-detect edge function
        const { data: geoData } = await supabase.functions.invoke("geo-detect");

        if (cancelled) return;

        const countryCode = geoData?.country_code || "NL";
        const countrySlug = COUNTRY_SLUG_MAP[countryCode] || "nl";
        const lat = browserGeo?.latitude ?? geoData?.latitude ?? null;
        const lng = browserGeo?.longitude ?? geoData?.longitude ?? null;
        const rawCitySlug = geoData?.city_slug || null;

        // Verify city exists in city_stats
        let detectedCitySlug: string | null = null;
        if (rawCitySlug) {
          const { data: cityMatch } = await supabase
            .from("city_stats")
            .select("city_slug")
            .eq("city_slug", rawCitySlug)
            .maybeSingle();
          if (cityMatch) {
            detectedCitySlug = cityMatch.city_slug;
          } else {
            const { data: fallbackCity } = await supabase
              .from("city_stats")
              .select("city_slug")
              .eq("country", countryCode)
              .order("studio_count", { ascending: false })
              .limit(1)
              .maybeSingle();
            detectedCitySlug = fallbackCity?.city_slug || null;
          }
        } else {
          const { data: fallbackCity } = await supabase
            .from("city_stats")
            .select("city_slug")
            .eq("country", countryCode)
            .order("studio_count", { ascending: false })
            .limit(1)
            .maybeSingle();
          detectedCitySlug = fallbackCity?.city_slug || null;
        }

        const locationData = {
          countryCode,
          countrySlug,
          city: geoData?.city || null,
          citySlug: rawCitySlug,
          latitude: lat,
          longitude: lng,
          detectedCitySlug,
        };

        // ── Save to localStorage with timestamp (24h TTL) ────
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ ...locationData, _ts: Date.now() }));
        } catch {}

        if (!cancelled) {
          setLocation({ ...locationData, loading: false });
        }
      } catch {
        if (!cancelled) {
          setLocation((prev) => ({ ...prev, loading: false }));
        }
      }
    }

    detect();
    return () => {
      cancelled = true;
    };
  }, []);

  return <LocationContext.Provider value={location}>{children}</LocationContext.Provider>;
}

export function useGeoLocation() {
  return useContext(LocationContext);
}
